import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const rgb = (name, stop) => `rgb(var(--c-${name}-${stop}))`;

export const VERDICT = {
  healthy: { label: "Healthy", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20", color: rgb("accent", 600) },
  mixed: { label: "Mixed", cls: "bg-amber-500/10 text-amber-600 border-amber-500/20", color: rgb("ochre", 500) },
  damaged: { label: "Damaged", cls: "bg-red-500/10 text-red-600 border-red-500/20", color: rgb("oxblood", 500) },
  insufficient: { label: "Not enough data", cls: "bg-slate-500/10 text-slate-500 border-slate-500/20", color: rgb("stone", 400) },
};

export function VerdictBadge({ verdict }) {
  const v = VERDICT[verdict] || VERDICT.insufficient;
  return (
    <span data-testid={`verdict-${verdict}`} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${v.cls}`}>
      {v.label}
    </span>
  );
}

export const scoreColor = (s) =>
  s == null ? rgb("stone", 400) : s >= 70 ? VERDICT.healthy.color : s >= 45 ? VERDICT.mixed.color : VERDICT.damaged.color;

export const COMPONENT_LABELS = {
  sentiment: "Sentiment",
  hostility: "Low hostility",
  reactions: "Reaction mix",
  engagement: "Engagement quality",
};

export function ComponentBars({ components }) {
  return (
    <div className="space-y-3">
      {Object.entries(COMPONENT_LABELS).map(([k, label]) => {
        const v = components?.[k];
        return (
          <div key={k} data-testid={`component-${k}`}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500 font-medium">{label}</span>
              <span className="text-slate-700 font-mono">{v == null ? "no data" : v}</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${v ?? 0}%`, background: scoreColor(v) }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export const TICK = { fontSize: 11, fill: rgb("stone", 500) };
export const GRID = rgb("stone", 200);
export const TOOLTIP = {
  contentStyle: { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12, color: "hsl(var(--foreground))" },
  labelStyle: { color: "hsl(var(--muted-foreground))" },
};

const RAIL = {
  high: "border-l-red-500 bg-red-50/60",
  medium: "border-l-amber-500 bg-amber-50/60",
  opportunity: "border-l-emerald-500 bg-emerald-50/60",
};
const SEV_LABEL = { high: "Act now", medium: "This week", opportunity: "Opportunity" };

// Which planner draft answers each next step; others have no draft to write.
const DRAFTABLE = {
  "damaged-post": ["reply", {}], "rising-theme": ["reply", {}], "unanswered-questions": ["reply", {}],
  "score-drop": ["crisis", { trigger: "score_drop" }], "threat-comments": ["crisis", { trigger: "threat" }],
  "coordinated-activity": ["crisis", { trigger: "coordinated" }], "bot-activity": ["crisis", { trigger: "coordinated" }],
};

export function NextSteps({ actions, limit, qs = "" }) {
  const shown = limit ? actions.slice(0, limit) : actions;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const draft = async (a) => {
    const [path, body] = DRAFTABLE[a.id];
    setBusy(a.id);
    try {
      await api.post(`/content/planner/${path}${qs}`, { ...body, detail: a.title });
      toast.success("Draft saved for review. Nothing is posted until you approve it.", { action: { label: "Open", onClick: () => navigate("/content?tab=planner") } });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  return (
    <ul className="space-y-3" data-testid="next-steps">
      {shown.map((a) => (
        <li key={a.id} data-testid={`action-${a.id}`} className={`rounded-lg border-l-2 px-4 py-3 ${RAIL[a.severity] || RAIL.opportunity}`}>
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-slate-900">{a.title}</p>
            <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 shrink-0">{SEV_LABEL[a.severity]}</span>
          </div>
          <p className="text-xs text-slate-600 mt-1">{a.why}</p>
          <ol className="mt-2 space-y-1 list-decimal list-inside text-[13px] text-slate-700">
            {a.steps.map((s) => <li key={s}>{s}</li>)}
          </ol>
          <div className="flex items-center gap-4 mt-2">
            <Link to={a.link.to} className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 underline underline-offset-2 hover:text-slate-900">
              {a.link.label} <ArrowUpRight className="w-3 h-3" />
            </Link>
            {DRAFTABLE[a.id] && user?.role !== "client_viewer" && (
              <button onClick={() => draft(a)} disabled={!!busy} data-testid={`draft-${a.id}`}
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50">
                {busy === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <PenLine className="w-3 h-3" />}Draft this response
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
